import styled from "styled-components"

import { useGetReferralAgentPlacementFunnelQuery } from "store/api/referralsApi"
import { ApiTypes } from "types"

import { Spinner } from "components/common/Spinner"

import { Card, CardHeader, CardTitle } from "./shared"

const segments: { label: string; key: keyof ApiTypes.Model.ReferralAgentPlacementFunnel; color: string }[] = [
  { label: "In review", key: "in_review", color: "var(--brand)" },
  { label: "Submitted", key: "submitted", color: "var(--status-info)" },
  { label: "In progress", key: "in_progress", color: "var(--status-warning)" },
  { label: "Installed", key: "installed", color: "var(--status-success)" },
]

const SegmentsBar = styled.div`
  display: flex;
  gap: 8px;
  min-height: 48px;
  margin-bottom: 24px;
  border-radius: 10px;
  background: var(--surface-3);
`

const Segment = styled.div<{ $width: number; $color: string }>`
  height: 48px;
  border-radius: 10px;
  flex: ${({ $width }) => $width} 0 0;
  min-width: 24px;
  background: ${({ $color }) => $color};
`

const TotalLabel = styled.div`
  text-align: center;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted);
`

const TotalValue = styled.div`
  margin: 4px 0 24px;
  text-align: center;
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  color: var(--text-primary);
`

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
`

const LegendItem = styled.div`
  flex: 1 0 0;
  min-width: 90px;
  text-align: center;
`

const LegendLabel = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  color: var(--text-muted);

  &::before {
    content: "";
    width: 12px;
    height: 12px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ $color }) => $color};
  }
`

const LegendValue = styled.div`
  margin-top: 4px;
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  color: var(--text-primary);
`

const Message = styled.p`
  margin: 0;
  font-size: 14px;
  color: var(--text-muted);
`

export const PlacementFunnelCard = () => {
  const { data, isLoading, isError } = useGetReferralAgentPlacementFunnelQuery()
  const funnel = data?.data

  const renderBody = () => {
    if (isLoading) return <Spinner />
    if (isError || !funnel) return <Message>The placement funnel is unavailable right now.</Message>

    return (
      <>
        {/* statuses with no thermostats get no segment; an empty funnel shows just the grey track */}
        <SegmentsBar>
          {segments
            .filter(({ key }) => funnel[key] > 0)
            .map(({ key, color }) => (
              <Segment key={key} data-testid="placement-funnel-segment" $width={funnel[key]} $color={color} />
            ))}
        </SegmentsBar>
        <TotalLabel>Total thermostats</TotalLabel>
        <TotalValue>{funnel.total}</TotalValue>
        <Legend>
          {segments.map(({ label, key, color }) => (
            <LegendItem key={key}>
              <LegendLabel $color={color}>{label}</LegendLabel>
              <LegendValue>{funnel[key]}</LegendValue>
            </LegendItem>
          ))}
        </Legend>
      </>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Placement funnel</CardTitle>
      </CardHeader>
      {renderBody()}
    </Card>
  )
}
